/**
 * BulkEnrollComplianceWizard
 *
 * Lets the user enroll many tasks against the same ComplianceType in one go,
 * instead of opening each task and adding the enrollment one-by-one. Saves
 * hours when onboarding 50 GST clients or 100 TDS retainers.
 *
 * Flow:
 *   Step 1 — pick a ComplianceType + a common start period (per-row override
 *            still possible). End period left blank by default = open-ended.
 *   Step 2 — pick the tasks. Each row gets its own TaxID input (PAN /
 *            GSTIN / TAN). For GST-type compliance, the TaxID auto-fills
 *            from the client's primary GSTIN; for everything else it
 *            stays blank for manual entry. Already-enrolled tasks for the
 *            selected type are flagged and pre-disabled.
 *   Step 3 — POST to /api/compliances/bulk-enroll, show per-row outcome.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { getTasks, getComplianceTypes, getClientGstins, bulkEnrollCompliances, triggerComplianceCron } from '../api';
import { showToast } from '../utils/toast';

// ─── Period helpers (Indian FY, Apr–Mar) ─────────────────────────────────

type Frequency = 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'ANNUAL' | string;

const pad2 = (n: number) => String(n).padStart(2, '0');

function fyLabel(startYear: number): string {
  return `FY ${startYear}-${pad2((startYear + 1) % 100)}`;
}

/** Convert a (year, period-index) → first-day-of-period 'YYYY-MM-DD'.
 *  Storage convention: the stored date is the first day of the period's
 *  first month, so the server-side cron's getUTCFullYear/getUTCMonth checks
 *  work regardless of frequency. */
function periodToDateString(year: number, period: number, freq: Frequency): string {
  if (freq === 'MONTHLY')   return `${year}-${pad2(period)}-01`;
  if (freq === 'QUARTERLY') {
    // period 1..4 → Apr(=4), Jul(=7), Oct(=10), Jan(=1 of next calendar year)
    const monthByQ = [4, 7, 10, 1];
    const m = monthByQ[period - 1] ?? 4;
    const y = period === 4 ? year + 1 : year;
    return `${y}-${pad2(m)}-01`;
  }
  if (freq === 'HALF_YEARLY') return `${year}-${period === 1 ? '04' : '10'}-01`;
  return `${year}-04-01`; // ANNUAL → start of FY
}

/** Reverse of periodToDateString: decode 'YYYY-MM-DD' into (year, period). */
function dateStringToPeriod(s: string | undefined | null, freq: Frequency): { year: number; period: number } | null {
  if (!s) return null;
  const [yStr, mStr] = s.split('-');
  const y = Number(yStr); const m = Number(mStr);
  if (!isFinite(y) || !isFinite(m)) return null;
  const fyStart = m >= 4 ? y : y - 1;
  if (freq === 'MONTHLY') return { year: y, period: m };
  if (freq === 'QUARTERLY') {
    let q = 1;
    if (m >= 4 && m <= 6) q = 1;
    else if (m >= 7 && m <= 9) q = 2;
    else if (m >= 10 && m <= 12) q = 3;
    else q = 4;
    return { year: fyStart, period: q };
  }
  if (freq === 'HALF_YEARLY') return { year: fyStart, period: (m >= 4 && m <= 9) ? 1 : 2 };
  return { year: fyStart, period: 1 };
}

/** Sensible default = current period (calendar month for MONTHLY, current
 *  quarter / half / FY otherwise). */
function defaultPeriodFor(freq: Frequency): string {
  const t = new Date();
  const cy = t.getFullYear();
  const cm = t.getMonth() + 1;
  if (freq === 'MONTHLY') return periodToDateString(cy, cm, 'MONTHLY');
  const fyStart = cm >= 4 ? cy : cy - 1;
  if (freq === 'QUARTERLY') {
    const q = cm >= 4 && cm <= 6 ? 1 : cm >= 7 && cm <= 9 ? 2 : cm >= 10 && cm <= 12 ? 3 : 4;
    return periodToDateString(fyStart, q, 'QUARTERLY');
  }
  if (freq === 'HALF_YEARLY') return periodToDateString(fyStart, cm >= 4 && cm <= 9 ? 1 : 2, 'HALF_YEARLY');
  return periodToDateString(fyStart, 1, 'ANNUAL');
}

/** Human-readable label e.g. "May 2026", "Q2 FY 2026-27", "H1 FY 2026-27", "FY 2026-27". */
function periodHumanLabel(value: string, freq: Frequency): string {
  const p = dateStringToPeriod(value, freq);
  if (!p) return '';
  if (freq === 'MONTHLY') {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[p.period - 1]} ${p.year}`;
  }
  if (freq === 'QUARTERLY')  return `Q${p.period} ${fyLabel(p.year)}`;
  if (freq === 'HALF_YEARLY') return `H${p.period} ${fyLabel(p.year)}`;
  return fyLabel(p.year);
}

// ─── PeriodPicker ─────────────────────────────────────────────────────────

const PeriodPicker: React.FC<{
  frequency: Frequency;
  value: string;
  onChange: (next: string) => void;
  allowEmpty?: boolean;
  size?: 'sm' | 'md';
}> = ({ frequency, value, onChange, allowEmpty, size = 'md' }) => {
  const baseCls = size === 'sm' ? 'text-xs py-1 px-1.5' : 'text-sm py-1.5 px-2';
  const selectCls = `input-field ${baseCls}`;
  const today = new Date();
  const cy = today.getFullYear();
  const cm = today.getMonth() + 1;
  const currentFy = cm >= 4 ? cy : cy - 1;

  // For MONTHLY use the native month input — universally supported and ergonomic.
  if (frequency === 'MONTHLY') {
    const monthVal = value ? value.slice(0, 7) : '';
    return (
      <input
        type="month"
        className={selectCls + ' w-40'}
        value={monthVal}
        onChange={(e) => onChange(e.target.value ? `${e.target.value}-01` : '')}
      />
    );
  }

  // For QUARTERLY / HALF_YEARLY / ANNUAL: FY-year dropdown + period dropdown.
  const parsed = dateStringToPeriod(value, frequency);
  const year = parsed?.year ?? currentFy;
  const period = parsed?.period ?? 1;
  const years = Array.from({ length: 7 }, (_, i) => currentFy - 3 + i);
  const emit = (y: number, p: number) => onChange(periodToDateString(y, p, frequency));

  return (
    <div className="flex gap-1">
      <select
        className={selectCls + ' w-28'}
        value={parsed ? String(year) : ''}
        onChange={(e) => {
          if (!e.target.value) { if (allowEmpty) onChange(''); return; }
          emit(Number(e.target.value), period);
        }}
      >
        {allowEmpty && <option value="">— select —</option>}
        {years.map((y) => <option key={y} value={y}>{fyLabel(y)}</option>)}
      </select>
      {frequency !== 'ANNUAL' && (
        <select
          className={selectCls + ' w-16'}
          value={parsed ? String(period) : '1'}
          onChange={(e) => emit(year, Number(e.target.value))}
          disabled={!parsed && !allowEmpty ? false : false}
        >
          {frequency === 'QUARTERLY' && [1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)}
          {frequency === 'HALF_YEARLY' && (<>
            <option value={1}>H1</option>
            <option value={2}>H2</option>
          </>)}
        </select>
      )}
    </div>
  );
};

interface CType { id: number; name: string; frequency: string; }
interface TaskRow {
  id: number;
  taskId: string;
  taskName: string;
  status: string;
  client?: { id: number; clientName: string };
  // Enriched on the client:
  primaryGstin?: string;        // pre-fetched on demand for GST-type compliance
  taxComplianceTypes?: Array<{ complianceTypeId: number }>; // existing enrollments
}

interface ClientGstin { gstin: string; label?: string | null; isPrimary?: boolean }

interface RowDraft {
  taskId: number;
  selected: boolean;
  taxId: string;                 // used for non-GST types / manual entry
  selectedGstins: string[];      // GST types: which of the client's GSTINs to enrol (one enrollment each)
  startPeriodOverride: string;   // optional, blank means use common
  alreadyEnrolled: boolean;
}

interface Props {
  onClose: () => void;
  onComplete?: () => void;        // called after a successful bulk-enroll
}

const isGstType = (name: string) => /\bgst\b|gstr/i.test(name || '');

const BulkEnrollComplianceWizard: React.FC<Props> = ({ onClose, onComplete }) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Step 1
  const [types, setTypes] = useState<CType[]>([]);
  const [typeId, setTypeId] = useState<number | ''>('');
  // Start/End now respect the chosen ComplianceType's frequency — MONTHLY uses
  // a month input, QUARTERLY/HALF_YEARLY/ANNUAL use FY-year + period dropdowns.
  // Start defaults to the current period for whichever frequency is active.
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');

  // Step 2
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [rows, setRows] = useState<Record<number, RowDraft>>({});
  // All GSTINs per client (GST-type compliance) so a client with multiple
  // registrations can be enrolled against each GSTIN.
  const [gstinsByClient, setGstinsByClient] = useState<Record<number, ClientGstin[]>>({});

  // Step 3
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ enrolled: number; skipped: number; failed: number; results: any[] } | null>(null);
  const [generatingAfter, setGeneratingAfter] = useState(false);

  // Load compliance types on open
  useEffect(() => {
    getComplianceTypes()
      .then((r) => setTypes((r.data as CType[]).filter((t: any) => t.isActive !== false)))
      .catch(() => showToast('Failed to load compliance types', 'error'));
  }, []);

  const selectedType = useMemo(() => types.find((t) => t.id === typeId), [types, typeId]);
  const freq: Frequency = selectedType?.frequency || 'MONTHLY';

  // When the user picks (or changes) a ComplianceType, reset Start to the
  // current period for that frequency, and clear End so it stays optional.
  // Without this, switching from a QUARTERLY type to a MONTHLY type (or vice
  // versa) would leave a date encoded for the wrong frequency.
  useEffect(() => {
    if (!selectedType) return;
    setStartPeriod(defaultPeriodFor(freq));
    setEndPeriod('');
    // Intentionally re-runs only when the chosen ComplianceType changes;
    // 'freq' is derived from selectedType and therefore changes in lockstep.
    // eslint-disable-next-line
  }, [selectedType?.id]);

  // Load tasks when stepping to 2. Fetch GSTINs lazily for clients in the list
  // if the chosen compliance type is GST-related.
  const goToStep2 = async () => {
    if (!typeId) return showToast('Pick a compliance type first', 'error');
    if (!startPeriod) return showToast('Set the start period', 'error');
    setTasksLoading(true);
    try {
      const res = await getTasks();
      // The /tasks endpoint returns Task[]. We also need to know which tasks
      // are already enrolled for this type — fetched per-task from
      // /tasks/:id/compliance-types is expensive, so we read it from the
      // bulk response only if the field is present. Otherwise the server
      // skips duplicates safely; we surface that on Step 3 as "Skipped".
      const list: TaskRow[] = (res.data as TaskRow[]).filter((t) => t.status === 'OPEN');
      setTasks(list);

      // For GST types, fetch ALL of each client's GSTINs so the user can enrol
      // the same compliance against each registration (e.g. Delhi + HP).
      const byClient: Record<number, ClientGstin[]> = {};
      if (selectedType && isGstType(selectedType.name)) {
        const uniqueClientIds = Array.from(new Set(list.map((t) => t.client?.id).filter(Boolean) as number[]));
        await Promise.all(
          uniqueClientIds.map(async (cid) => {
            try {
              const r = await getClientGstins(cid);
              const gstins = (r.data as any[])
                .filter((g) => g.gstin)
                .map((g) => ({ gstin: g.gstin as string, label: g.label, isPrimary: !!g.isPrimary }));
              if (gstins.length) byClient[cid] = gstins;
            } catch { /* ignore */ }
          }),
        );
      }
      setGstinsByClient(byClient);

      const initial: Record<number, RowDraft> = {};
      for (const t of list) {
        const gstins = t.client?.id ? byClient[t.client.id] : undefined;
        // Default selection = the primary GSTIN (or the first one). The user can
        // tick additional GSTINs per task on this step.
        const defaultGstin = gstins ? (gstins.find((g) => g.isPrimary)?.gstin || gstins[0]?.gstin) : undefined;
        initial[t.id] = {
          taskId: t.id,
          selected: false,
          taxId: '',
          selectedGstins: defaultGstin ? [defaultGstin] : [],
          // Auto-fill per-row override with the common Start period chosen on
          // Step 1. The user can still change individual rows where needed.
          startPeriodOverride: startPeriod,
          alreadyEnrolled: false,
        };
      }
      setRows(initial);
      setStep(2);
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Failed to load tasks', 'error');
    } finally {
      setTasksLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (filterClient && (t.client?.id ?? 0) !== Number(filterClient)) return false;
      if (!q) return true;
      return (
        t.taskId.toLowerCase().includes(q) ||
        t.taskName.toLowerCase().includes(q) ||
        (t.client?.clientName || '').toLowerCase().includes(q)
      );
    });
  }, [tasks, search, filterClient]);

  const clientOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of tasks) if (t.client) map.set(t.client.id, t.client.clientName);
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [tasks]);

  const selectedCount = useMemo(
    () => filtered.filter((t) => rows[t.id]?.selected).length,
    [filtered, rows],
  );

  const isGst = !!(selectedType && isGstType(selectedType.name));

  /** GSTINs available for a task's client (GST types only). */
  const gstinsForTask = (t: TaskRow): ClientGstin[] =>
    isGst && t.client?.id ? (gstinsByClient[t.client.id] || []) : [];

  // Total enrollments that will be created across ALL selected tasks (a task
  // with two ticked GSTINs counts as two).
  const totalEnrollments = useMemo(() => {
    let n = 0;
    for (const t of tasks) {
      const r = rows[t.id];
      if (!r?.selected) continue;
      const gstins = gstinsForTask(t);
      n += gstins.length ? r.selectedGstins.length : (r.taxId.trim() ? 1 : 0);
    }
    return n;
    // eslint-disable-next-line
  }, [tasks, rows, isGst, gstinsByClient]);

  const setRow = (id: number, patch: Partial<RowDraft>) =>
    setRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const toggleRowGstin = (id: number, gstin: string) =>
    setRows((prev) => {
      const r = prev[id];
      if (!r) return prev;
      const has = r.selectedGstins.includes(gstin);
      return {
        ...prev,
        [id]: { ...r, selectedGstins: has ? r.selectedGstins.filter((g) => g !== gstin) : [...r.selectedGstins, gstin] },
      };
    });

  const selectAllVisible = (sel: boolean) =>
    setRows((prev) => {
      const next = { ...prev };
      for (const t of filtered) next[t.id] = { ...next[t.id], selected: sel };
      return next;
    });

  // Step 3: submit
  const submit = async () => {
    const chosen = Object.values(rows).filter((r) => r.selected);
    if (chosen.length === 0) return showToast('Select at least one task', 'error');

    // Expand each selected task into one enrollment row per chosen GSTIN
    // (GST types), or a single row from the manual Tax ID (non-GST types).
    const enrollRows: { taskId: number; taxId: string; startPeriod: string; endPeriod?: string }[] = [];
    const missing: string[] = [];
    for (const r of chosen) {
      const task = tasks.find((t) => t.id === r.taskId);
      const gstins = task ? gstinsForTask(task) : [];
      const start = r.startPeriodOverride || startPeriod;
      const label = task?.taskId || `#${r.taskId}`;
      if (gstins.length) {
        if (r.selectedGstins.length === 0) { missing.push(label); continue; }
        for (const g of r.selectedGstins) enrollRows.push({ taskId: r.taskId, taxId: g, startPeriod: start, endPeriod: endPeriod || undefined });
      } else {
        if (!r.taxId.trim()) { missing.push(label); continue; }
        enrollRows.push({ taskId: r.taskId, taxId: r.taxId.trim(), startPeriod: start, endPeriod: endPeriod || undefined });
      }
    }
    if (missing.length > 0) {
      return showToast(`${missing.length} selected task(s) have no GSTIN / Tax ID selected: ${missing.slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`, 'error');
    }
    if (enrollRows.length === 0) return showToast('Nothing to enrol', 'error');

    setSubmitting(true);
    try {
      const res = await bulkEnrollCompliances({ complianceTypeId: Number(typeId), rows: enrollRows });
      setResult(res.data);
      setStep(3);
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Bulk enrol failed', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const runGenerate = async () => {
    setGeneratingAfter(true);
    try {
      await triggerComplianceCron();
      showToast('✓ Compliance instances generated for the most recent period', 'success');
      onComplete?.();
      onClose();
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Generation failed (admin only?)', 'error');
    } finally {
      setGeneratingAfter(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">🪄 Bulk Enroll Tasks for Compliance</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Enroll many tasks against the same compliance type in one batch — saves opening each task individually.
            </p>
          </div>
          <button className="text-gray-400 hover:text-gray-600 text-2xl leading-none" onClick={onClose}>✕</button>
        </div>

        {/* Step indicator */}
        <div className="px-6 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${step === s ? 'text-purple-600 font-semibold' : step > s ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    step === s ? 'bg-purple-100 border-2 border-purple-600' :
                    step > s ? 'bg-green-100 border-2 border-green-600' :
                    'bg-gray-100 border-2 border-gray-300'
                  }`}>{step > s ? '✓' : s}</div>
                  <span className="text-sm">
                    {s === 1 ? 'Compliance Type' : s === 2 ? 'Pick Tasks' : 'Result'}
                  </span>
                </div>
                {s < 3 && <div className={`flex-1 h-px ${step > s ? 'bg-green-300' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">

          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="label">Compliance Type *</label>
                <select className="input-field" value={typeId} onChange={(e) => setTypeId(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">— Select compliance type —</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.frequency})</option>
                  ))}
                </select>
                {selectedType && isGstType(selectedType.name) && (
                  <p className="mt-1 text-xs text-blue-700">
                    GST-type detected — each client's GSTINs are listed on the next step. The primary is pre-ticked; tick additional GSTINs to enrol a client's multiple registrations (e.g. Delhi + HP) in one go.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Common Start Period *</label>
                  <PeriodPicker frequency={freq} value={startPeriod} onChange={setStartPeriod} />
                  <p className="mt-1 text-[11px] text-gray-500">
                    First <strong>{freq.toLowerCase().replace('_', '-')}</strong> period to bill from
                    {startPeriod ? <> — <span className="text-gray-700">{periodHumanLabel(startPeriod, freq)}</span></> : null}.
                  </p>
                </div>
                <div>
                  <label className="label">End Period <span className="text-gray-400">(optional)</span></label>
                  <PeriodPicker frequency={freq} value={endPeriod} onChange={setEndPeriod} allowEmpty />
                  <p className="mt-1 text-[11px] text-gray-500">
                    Leave blank for open-ended (ongoing retainer)
                    {endPeriod ? <> — last period: <span className="text-gray-700">{periodHumanLabel(endPeriod, freq)}</span></> : null}.
                  </p>
                </div>
              </div>

              <div className="mt-2 rounded-md bg-blue-50 p-3 text-xs text-blue-900">
                <strong>What this does:</strong> creates one <em>enrollment</em> per selected task linking it to <em>{selectedType?.name || 'the chosen compliance type'}</em>. The compliance instances themselves (one per period: e.g. Jul-2026, Aug-2026) are then auto-generated by the monthly cron — or you can click "Generate Now" on the next screen after enrolling.
              </div>
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <input className="input-field w-56 text-sm" placeholder="Search task / client…" value={search}
                  onChange={(e) => setSearch(e.target.value)} />
                <select className="input-field w-52 text-sm" value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}>
                  <option value="">All Clients</option>
                  {clientOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
                <div className="ml-auto flex gap-2 items-center text-sm">
                  <button onClick={() => selectAllVisible(true)} className="text-xs text-blue-600 hover:underline">Select all visible</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={() => selectAllVisible(false)} className="text-xs text-gray-500 hover:underline">Deselect all</button>
                  <span className="ml-3 text-gray-700">Selected: <b>{selectedCount}</b>{isGst && <> · enrollments: <b>{totalEnrollments}</b></>}</span>
                </div>
              </div>

              {tasksLoading ? (
                <div className="text-center py-12 text-gray-400">Loading tasks…</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  No open tasks match. Try clearing filters.
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-2 w-8 text-center">
                          <input type="checkbox"
                            checked={filtered.length > 0 && filtered.every((t) => rows[t.id]?.selected)}
                            onChange={(e) => selectAllVisible(e.target.checked)} />
                        </th>
                        <th className="px-2 py-2 text-left">Task</th>
                        <th className="px-2 py-2 text-left">Client</th>
                        <th className="px-2 py-2 text-left">
                          {isGst ? <>GSTIN(s) <span className="text-gray-400 font-normal">(tick one or more)</span></> : <>Tax ID <span className="text-gray-400 font-normal">(PAN / GSTIN / TAN)</span></>}
                        </th>
                        <th className="px-2 py-2 text-left">Start (override)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((t) => {
                        const row = rows[t.id];
                        if (!row) return null;
                        return (
                          <tr key={t.id} className={`border-t border-gray-100 ${row.selected ? '' : 'opacity-60'}`}>
                            <td className="px-2 py-1.5 text-center">
                              <input type="checkbox" checked={row.selected}
                                onChange={(e) => setRow(t.id, { selected: e.target.checked })} />
                            </td>
                            <td className="px-2 py-1.5">
                              <div className="font-mono text-blue-600 font-medium">{t.taskId}</div>
                              <div className="text-gray-500 truncate max-w-[200px]" title={t.taskName}>{t.taskName}</div>
                            </td>
                            <td className="px-2 py-1.5 text-gray-700 truncate max-w-[180px]" title={t.client?.clientName || ''}>
                              {t.client?.clientName || '—'}
                            </td>
                            <td className="px-2 py-1.5">
                              {(() => {
                                const gstins = gstinsForTask(t);
                                if (gstins.length) {
                                  return (
                                    <div className="space-y-1">
                                      {gstins.map((g) => (
                                        <label key={g.gstin} className="flex items-center gap-1.5 cursor-pointer">
                                          <input type="checkbox" checked={row.selectedGstins.includes(g.gstin)}
                                            onChange={() => toggleRowGstin(t.id, g.gstin)} />
                                          <span className="font-mono text-[11px] text-gray-700">{g.gstin}</span>
                                          {g.isPrimary && <span className="text-[10px] text-indigo-600 bg-indigo-50 border border-indigo-100 rounded px-1">primary</span>}
                                          {g.label && <span className="text-[10px] text-gray-400 truncate max-w-[90px]" title={g.label}>{g.label}</span>}
                                        </label>
                                      ))}
                                    </div>
                                  );
                                }
                                return (
                                  <input className="input-field text-xs py-1 w-48" value={row.taxId}
                                    placeholder={isGst ? 'No GSTIN on file — enter manually' : 'PAN / GSTIN / TAN'}
                                    onChange={(e) => setRow(t.id, { taxId: e.target.value })} />
                                );
                              })()}
                            </td>
                            <td className="px-2 py-1.5">
                              <PeriodPicker
                                frequency={freq}
                                value={row.startPeriodOverride}
                                onChange={(v) => setRow(t.id, { startPeriodOverride: v })}
                                size="sm"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && result && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-4xl mb-2">✅</div>
                <h3 className="text-lg font-bold text-green-800">Bulk Enrolment Complete</h3>
                <p className="text-sm text-green-700 mt-1">
                  Enrolled <strong>{result.enrolled}</strong>, skipped <strong>{result.skipped}</strong> (already enrolled), failed <strong>{result.failed}</strong>.
                </p>
              </div>

              {(result.failed > 0 || result.skipped > 0) && (
                <details className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs">
                  <summary className="cursor-pointer font-semibold text-gray-700">
                    Per-row outcome ({result.results.length})
                  </summary>
                  <div className="mt-2 space-y-0.5 max-h-64 overflow-y-auto">
                    {result.results.map((r: any, i: number) => {
                      const t = tasks.find((x) => x.id === r.taskId);
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className={
                            r.status === 'ENROLLED' ? 'text-green-600' :
                            r.status === 'SKIPPED'  ? 'text-amber-600' : 'text-red-600'
                          }>
                            {r.status === 'ENROLLED' ? '✓' : r.status === 'SKIPPED' ? '⊘' : '✗'}
                          </span>
                          <span className="font-mono">{t?.taskId || `#${r.taskId}`}</span>
                          {r.taxId && <span className="font-mono text-indigo-500">{r.taxId}</span>}
                          <span className="text-gray-500 truncate max-w-sm">{t?.taskName || ''}</span>
                          <span className="text-gray-400">— {r.status}</span>
                          {r.reason && <span className="italic text-gray-500">({r.reason})</span>}
                        </div>
                      );
                    })}
                  </div>
                </details>
              )}

              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
                <strong>Next step:</strong> compliance <em>instances</em> for each period are generated by the
                monthly cron at the start of every month. To generate them right
                now for the most recent period, click "Generate Instances Now"
                below. (Admin only.)
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between">
          <div>
            {step > 1 && step < 3 && (
              <button className="btn-secondary text-sm" onClick={() => setStep((step - 1) as 1 | 2)}>← Back</button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <>
                <button className="btn-secondary text-sm" onClick={onClose}>Cancel</button>
                <button className="btn-primary text-sm" onClick={goToStep2} disabled={!typeId || tasksLoading}>
                  {tasksLoading ? 'Loading tasks…' : 'Next: Pick Tasks →'}
                </button>
              </>
            )}
            {step === 2 && (
              <>
                <button className="btn-secondary text-sm" onClick={onClose} disabled={submitting}>Cancel</button>
                <button className="btn-primary text-sm" onClick={submit} disabled={submitting || totalEnrollments === 0}>
                  {submitting ? 'Enrolling…' : `Enroll ${totalEnrollments} enrollment${totalEnrollments === 1 ? '' : 's'}`}
                </button>
              </>
            )}
            {step === 3 && (
              <>
                <button className="btn-secondary text-sm" onClick={onClose}>Close</button>
                <button className="btn-primary text-sm" onClick={runGenerate} disabled={generatingAfter}>
                  {generatingAfter ? 'Generating…' : '⚡ Generate Instances Now'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkEnrollComplianceWizard;
